/**
 * Lesezugriff auf v5_zone_signals — für Dashboard und Auswertung.
 *
 * Getrennt von zone-signals.ts (reine Logik, keine DB), damit die
 * Zustandsmaschinen-Funktionen weiterhin ohne Supabase-Abhängigkeit
 * testbar und im Server-Cron nutzbar bleiben.
 */
import { supabase } from '@/lib/supabase';
import type { SignalKennzahlen } from './signal-begruendung';

export interface OffenesZonenSignal {
  id: number;
  symbol: string;
  ausloeser: string;
  entry_price: number;
  stop_price: number;
  generated_date: string;
}

export interface ZonenBilanz {
  gesamt: number;
  ausgestoppt: number;
  zeitausstieg: number;
  /** Durchschnittliches Ergebnis in R über alle aufgelösten Signale. */
  schnitt_r: number | null;
  /** Durchschnittliches Ergebnis in Prozent. */
  schnitt_pct: number | null;
}

export async function fetchOffeneZonenSignale(
  signal?: AbortSignal
): Promise<OffenesZonenSignal[]> {
  const { data, error } = await supabase
    .from('v5_zone_signals')
    .select('id, symbol, ausloeser, entry_price, stop_price, generated_date')
    .eq('status', 'offen')
    // Krypto raus (Audit 10.08.2026). Der Cron erzeugt seit dem Riegel in
    // server-score-cron.ts keine Krypto-Zonen mehr, aber Altbestand aus der
    // Zeit davor liegt noch in der Tabelle. `leiteHandlungenAb` filterte ihn
    // bereits, die Karte „Offene Kaufzonen" nicht — dort standen sie weiter
    // als kaufbar. Alle Rueckrechnungen dieses Projekts schliessen Symbole
    // mit „/" aus; eine Kaufzone auf einem nie geprueften Instrument darf
    // nirgends angezeigt werden.
    .not('symbol', 'like', '%/%')
    .order('generated_date', { ascending: false })
    .limit(20)
    .abortSignal(signal!);
  if (error || !data) return [];
  return data.map((r) => ({
    id: Number(r.id),
    symbol: String(r.symbol),
    ausloeser: String(r.ausloeser),
    entry_price: Number(r.entry_price),
    stop_price: Number(r.stop_price),
    generated_date: String(r.generated_date),
  }));
}

/**
 * Bilanz der aufgelösten Signale — die Zahl, die ein Signal laut §5b
 * Kriterium 5 („trägt ihre Bilanz sichtbar") mitbringen MUSS. Ohne Nenner
 * ist jede Trefferquote nur eine Behauptung.
 */
export async function fetchZonenBilanz(signal?: AbortSignal): Promise<ZonenBilanz | null> {
  const { data, error } = await supabase
    .from('v5_zone_signals')
    .select('status, outcome_r, outcome_pct')
    .neq('status', 'offen')
    // Auch hier: Die Bilanz einer Regel darf keine Instrumente enthalten, auf
    // denen die Regel nie geprueft wurde — sonst misst die Trefferquote etwas
    // anderes als das, was sie behauptet.
    .not('symbol', 'like', '%/%')
    .limit(1000)
    .abortSignal(signal!);
  if (error || !data || data.length === 0) return null;
  const rs = data.map((r) => Number(r.outcome_r)).filter((n) => Number.isFinite(n));
  const pcts = data.map((r) => Number(r.outcome_pct)).filter((n) => Number.isFinite(n));
  return {
    gesamt: data.length,
    ausgestoppt: data.filter((r) => r.status === 'ausgestoppt').length,
    zeitausstieg: data.filter((r) => r.status === 'zeitausstieg').length,
    schnitt_r: rs.length > 0 ? rs.reduce((a, b) => a + b, 0) / rs.length : null,
    schnitt_pct: pcts.length > 0 ? pcts.reduce((a, b) => a + b, 0) / pcts.length : null,
  };
}

/**
 * Ein Signal mit seinem VOLLSTÄNDIGEN Lebenslauf.
 *
 * Ronni am 11.08.: „Ich fänd's gut, wenn ich auch da sehe, welche Werte er
 * eigentlich genommen hat, wann er eingestiegen ist, wann er ausgestiegen
 * ist, was zum Stop geführt hat. Das ist jetzt nicht sichtbar."
 *
 * Es war nie sichtbar — obwohl alles davon seit dem 07.08. in der Tabelle
 * steht. Angezeigt wurden bisher nur die offenen Signale und eine
 * Gesamtbilanz aus Durchschnitten. Ein Durchschnitt sagt nicht, WELCHER Wert
 * schiefging.
 */
export interface ZonenSignalVerlauf {
  id: number;
  symbol: string;
  ausloeser: string;
  entry_price: number;
  stop_price: number;
  generated_date: string;
  status: string;
  /** Tag und Preis des Teilverkaufs bei +2R. null = nie erreicht. */
  teil_raus_at: string | null;
  teil_raus_price: number | null;
  outcome_at: string | null;
  outcome_price: number | null;
  outcome_r: number | null;
  outcome_pct: number | null;
  /** Zustand zum Zeitpunkt der Eröffnung — Grundlage der Begründung. */
  kennzahlen: SignalKennzahlen | null;
}

export async function fetchZonenVerlauf(
  signal?: AbortSignal,
  limit = 200
): Promise<ZonenSignalVerlauf[]> {
  const { data, error } = await supabase
    .from('v5_zone_signals')
    // Bewusst `*`: Die Spalten teil_raus_at/teil_raus_price kamen erst mit
    // einer spaeteren Migration dazu. Eine namentliche Liste wuerde bei einer
    // Datenbank ohne diese Migration mit PostgREST-Fehler 42703 abbrechen und
    // die ganze Ansicht leeren — derselbe Fehler wie am 08.08. im Cron.
    .select('*')
    .not('symbol', 'like', '%/%')
    .order('generated_date', { ascending: false })
    .limit(limit)
    .abortSignal(signal!);
  if (error || !data) return [];
  const z = (v: unknown): number | null => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return data.map((r) => ({
    id: Number(r.id),
    symbol: String(r.symbol),
    ausloeser: String(r.ausloeser),
    entry_price: Number(r.entry_price),
    stop_price: Number(r.stop_price),
    generated_date: String(r.generated_date),
    status: String(r.status),
    teil_raus_at: r.teil_raus_at ? String(r.teil_raus_at) : null,
    teil_raus_price: z(r.teil_raus_price),
    outcome_at: r.outcome_at ? String(r.outcome_at).slice(0, 10) : null,
    outcome_price: z(r.outcome_price),
    outcome_r: z(r.outcome_r),
    outcome_pct: z(r.outcome_pct),
    kennzahlen: (r.kennzahlen ?? null) as SignalKennzahlen | null,
  }));
}
